<!--
	/case-studies/ram-1500-fender-flare — the first customer story.

	STEK Estonia had a RAM 1500 on bolt-on fender flares in the bay and no
	pattern anywhere that fit them. They scanned one flare and sent the mesh; we
	ran it through PPF Engine and sent back cut files. The page tells that in
	the order it happened: the part under the scanner (the real scan, live in the
	hero), the problem in photos, the delivered file drawing itself, the film on
	the flare.

	Every figure is read off the job files: the raw scan's triangle count, the
	exported SVG's dimensions, cut length and cutouts. The customer is named
	here on purpose; this route is the one documented exception to the brand
	gate (scripts/content-gates.mjs).
-->
<script lang="ts">
	import Logotype from '$lib/components/ui/Logotype.svelte';
	import { Nav, Footer } from '$lib/components/sections';
	import {
		Button,
		MonoLabel,
		Readout,
		Reveal,
		ScrubWords,
		Section,
		SectionHead
	} from '$lib/components/ui';
	import ScanPass from '$lib/components/three/ScanPass.svelte';
	import Meta from '$lib/seo/Meta.svelte';
	import { pricing } from '$lib/content/pricing';
	import TemplateSheet from './TemplateSheet.svelte';

	const description =
		'No pattern library had the bolt-on fender flares on a RAM 1500. STEK Estonia sent one 3D scan; PPF Engine sent back a plotter-ready cut file with all nine bolt recesses.';

	const flare = {
		model: '/models/ram-flare.glb',
		// The scan's chord runs 40° off level in its own file: roll squares the arch up.
		rotation: [-0.06, -0.3, 0.7] as [number, number, number],
		scale: 0.5,
		position: [0.25, -0.08, 0] as [number, number, number],
		aim: [0.15, 0.8, 0] as [number, number, number],
		metal: ['#3a3e40', '#b4b8b0'] as [string, string]
	};

	const obstacles = [
		{
			index: '01',
			label: 'Aftermarket part',
			value: 'Libraries draw the car, not what was bolted to it.',
			body: 'Every pattern database has a RAM 1500 front fender. None has this flare. The part came from a catalog the pattern makers never see.'
		},
		{
			index: '02',
			label: 'Nine bolt recesses',
			value: 'Each opening is a place for the film to fail.',
			body: 'The flare is held by nine bolts, each sunk in its own pocket. Film bridged over a pocket lifts. Film trimmed around one by hand means a blade next to gloss black.'
		},
		{
			index: '03',
			label: 'A 1.5 metre arch',
			value: 'Too long to guess, too curved to measure with a tape.',
			body: 'The flare runs the whole wheel arch and rolls over its own edge. Bulk film on a shape like that is a slow install and a lot of film on the floor.'
		}
	] as const;

	const steps = [
		{
			index: '01',
			title: 'STEK Estonia scanned the flare',
			body: 'One pass with a handheld structured-light scanner, on the truck, in the bay. The mesh came to us as a single STL: 11,830,927 triangles.'
		},
		{
			index: '02',
			title: 'PPF Engine flattened it',
			body: 'The curved surface was unrolled into one flat piece, 1485 × 435 mm, with five relief notches where the flare turns hardest. About three minutes of processing.'
		},
		{
			index: '03',
			title: 'The recesses were traced, not assumed',
			body: 'The first draft used a 38 mm circle for every bolt pocket. The second reads each pocket lip straight off the scan and offsets it 3 mm, because no two of the nine are the same shape.'
		},
		{
			index: '04',
			title: 'Cut files went back',
			body: 'SVG, DXF and PLT, in real millimetres, exactly as exported. Loaded on the plotter, cut from the roll, installed on the truck.'
		}
	] as const;

	const files = [
		{
			ext: '.svg',
			title: 'Editable vector',
			body: 'One outline, nine cutouts, real millimetres. Opens in Illustrator, Inkscape or any plotter front end.'
		},
		{
			ext: '.dxf',
			title: 'Cutter and CAD software',
			body: 'The same geometry for the cutting software the shop already runs, outline and cutouts on their own layers.'
		},
		{
			ext: '.plt',
			title: 'Straight to the plotter',
			body: 'HPGL for Graphtec-class cutters. No conversion step between the email and the roll.'
		},
		{
			ext: '.ppf',
			title: 'The whole session',
			body: 'The PPF Engine project: scan, flatten and every edit, still adjustable. The next RAM on these flares is a re-cut, not a redo.'
		}
	] as const;

	const argument = [
		'A black RAM 1500 on bolt-on fender flares. The customer wanted the flares protected too. No pattern library had them, and nobody wanted a knife anywhere near that paint.',
		'So the flare itself became the pattern.'
	];
</script>

<Meta
	title="Case study: STEK Estonia and the RAM 1500 fender flare"
	{description}
	video="ram-flare"
/>

<Nav tone="dark" />
<main>
	<!-- ── Hero: the scanned flare, under the scanner ──────────────────── -->
	<header class="hero sec--dark" data-tone="dark">
		<ScanPass
			options={flare}
			poster="/img/ram-flare-poster.webp"
			hud="RAM 1500 FENDER FLARE · STRUCTURED LIGHT · 11,830,927 TRIANGLES"
		/>
		<div class="hero__shade" aria-hidden="true"></div>

		<div class="hero__inner">
			<Reveal class="cs-hero-copy">
				<p class="lockup">
					<img
						src="/img/stek-estonia-white.svg"
						alt="STEK Estonia"
						width="431"
						height="109"
						class="lockup__logo"
					/>
					<span class="lockup__x" aria-hidden="true">×</span>
					<span class="lockup__us"><span class="sr-only">PPF Engine</span><Logotype /></span>
				</p>
				<p class="kicker">
					<span class="kicker__tick" aria-hidden="true"></span>
					Case study · RAM 1500 · bolt-on fender flare
				</p>
				<h1>No pattern fit the flare. So the flare became the pattern.</h1>
				<p class="lede">
					STEK Estonia had the truck in the bay and nothing to cut. They sent us one 3D scan. We
					sent back a plotter-ready file with all nine bolt recesses in it.
				</p>
				<div class="actions">
					<Button href="/case-studies/ram-1500-fender-flare#file" size="lg">See the cut file</Button
					>
					<Button href="/ppf-pattern-software" variant="dark" size="lg">How PPF Engine works</Button
					>
				</div>
			</Reveal>
		</div>

		<div class="hero__figures">
			<Reveal class="cs-figures" delay={200}>
				<Readout label="The scan" value="11,830,927 triangles" size="md" accent countUp />
				<Readout label="The template" value="1485 × 435 mm" size="md" />
				<Readout label="Bolt recesses" value="9 traced" size="md" accent />
				<Readout label="Delivered as" value="SVG · DXF · PLT" size="md" />
			</Reveal>
		</div>
	</header>

	<!-- ── The job ─────────────────────────────────────────────────────── -->
	<Section id="job">
		<div class="job">
			<div class="job__copy">
				<SectionHead
					index="01"
					label="The job"
					heading="The truck was in the bay. The pattern did not exist."
				/>
				<div class="argument">
					<ScrubWords text={argument[0]} />
					<ScrubWords text={argument[1]} />
				</div>
			</div>
			<Reveal class="cs-photo cs-photo--truck" y={20}>
				<img
					src="/img/ram-truck.webp"
					alt="A black RAM 1500 in the STEK Estonia workshop, hood up, with gloss black bolt-on fender flares around the front wheel arch."
					width="1400"
					height="2489"
					loading="lazy"
					decoding="async"
				/>
				<p class="photo__cap">STEK Estonia workshop · RAM 1500 · bolt-on flares, gloss black</p>
			</Reveal>
		</div>

		<ul class="ledger">
			{#each obstacles as item, i (item.label)}
				<Reveal as="li" delay={i * 70}>
					<p class="ledger__index">{item.index}</p>
					<p class="ledger__label">{item.label}</p>
					<h3 class="ledger__title">{item.value}</h3>
					<p class="ledger__body">{item.body}</p>
				</Reveal>
			{/each}
		</ul>
	</Section>

	<!-- ── The file ────────────────────────────────────────────────────── -->
	<Section id="file" tone="dark" grid>
		<div class="file-head">
			<div>
				<p class="kicker">
					<span class="kicker__tick" aria-hidden="true"></span>
					02 · The cut file
				</p>
				<h2 class="dark-h2">One scan in. One file out.</h2>
				<p class="lede">
					This is the file STEK Estonia cut, drawn here from the exported SVG itself. Nobody redrew
					it. The outline is exactly what came out of PPF Engine after about three minutes of
					processing: no anchor moved, no curve cleaned up by hand. Point at a bolt recess to put it
					under the loupe, and switch drafts to see why a circle was not good enough.
				</p>
			</div>
			<Reveal class="cs-photo cs-photo--recess" y={20}>
				<img
					src="/img/ram-film.webp"
					alt="The cut film being laid over the gloss black fender flare, the plotter-cut openings lining up with the bolt pockets."
					width="1200"
					height="2109"
					loading="lazy"
					decoding="async"
				/>
				<p class="photo__cap photo__cap--dark">going on: the cut openings landing on the pockets</p>
			</Reveal>
		</div>

		<TemplateSheet />

		<Reveal class="cs-asis" delay={80}>
			<Readout label="Processing" value="≈ 3 min" size="md" accent />
			<Readout label="Edits after export" value="None" size="md" />
			<Readout label="Outline" value="182 Bézier segments" size="md" />
			<Readout label="Cut length" value="4,561 mm" size="md" />
		</Reveal>

		<ol class="steps">
			{#each steps as step, i (step.index)}
				<Reveal as="li" delay={i * 70}>
					<p class="steps__index">{step.index}</p>
					<h3 class="steps__title">{step.title}</h3>
					<p class="steps__body">{step.body}</p>
				</Reveal>
			{/each}
		</ol>
	</Section>

	<!-- ── What went back ──────────────────────────────────────────────── -->
	<Section id="delivered" tone="panel">
		<SectionHead
			index="03"
			label="What went back"
			heading="Files the plotter already understands."
			intro="No proprietary format and nothing to convert. The same geometry four ways, from the vector you can edit to the session you can reopen."
		/>
		<ul class="ledger ledger--files">
			{#each files as file, i (file.ext)}
				<Reveal as="li" delay={i * 60}>
					<p class="ledger__index ledger__index--ext">{file.ext}</p>
					<h3 class="ledger__title">{file.title}</h3>
					<p class="ledger__body">{file.body}</p>
				</Reveal>
			{/each}
		</ul>
	</Section>

	<!-- ── The result ──────────────────────────────────────────────────── -->
	<Section id="result">
		<div class="result">
			<Reveal class="cs-photo cs-photo--film" y={20}>
				<img
					src="/img/ram-recesses.webp"
					alt="The finished fender flare under paint protection film: gloss black, wet from the install, with a clean opening around every bolt pocket."
					width="1200"
					height="2115"
					loading="lazy"
					decoding="async"
				/>
				<p class="photo__cap">film installed · a clean opening around every bolt pocket</p>
			</Reveal>
			<div class="result__copy">
				<SectionHead index="04" label="The result" heading="The job stayed in the shop." />
				<p class="result__lead">
					The flare went on in one piece, with the nine openings already where the bolts are. No
					hand templating on the truck, and no blade trimming around a pocket on gloss black paint.
				</p>
				<dl class="result__rows">
					<div>
						<dt>Before</dt>
						<dd>No pattern in any library. Bulk film and a knife, or turn the flares down.</dd>
					</div>
					<div>
						<dt>After</dt>
						<dd>One piece, 1485 × 435 mm, nine cutouts, cut on the shop's own plotter.</dd>
					</div>
					<div>
						<dt>Next time</dt>
						<dd>The template is on file. The next RAM on these flares is a re-cut.</dd>
					</div>
				</dl>
				<MonoLabel>The customer</MonoLabel>
				<a class="customer" href="https://stek.ee" rel="noopener" target="_blank">
					<img
						src="/img/stek-estonia.svg"
						alt="STEK Estonia"
						width="431"
						height="109"
						loading="lazy"
					/>
					<span>PPF studio and distributor, Estonia · stek.ee</span>
				</a>
			</div>
		</div>
	</Section>

	<!-- ── The close ───────────────────────────────────────────────────── -->
	<Section id="next" tone="dark" grid>
		<div class="close">
			<p class="kicker">
				<span class="kicker__tick" aria-hidden="true"></span>
				Your bay, next
			</p>
			<h2 class="dark-h2 dark-h2--xl">The car in your bay is the pattern.</h2>
			<p class="lede">
				PPF Engine is the software that did this job: scan, flatten, check, cut. It ships to
				installers in {pricing.deliveryWindow}, and the presale is open.
			</p>
			<div class="actions">
				<Button href="/#pricing" size="lg">See the presale</Button>
				<Button href="/ppf-pattern-software#demo" variant="dark" size="lg"
					>Request a workflow demo</Button
				>
			</div>
		</div>
	</Section>
</main>
<Footer />

<style>
	/* ── Hero ─────────────────────────────────────────────────────────── */
	.hero {
		position: relative;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		min-height: min(100svh, 62rem);
		background: var(--color-app-950);
		overflow: hidden;
	}
	.hero__shade {
		position: absolute;
		inset: 0;
		background:
			linear-gradient(90deg, var(--color-app-950) 0%, rgba(12, 13, 11, 0.6) 30%, transparent 52%),
			linear-gradient(0deg, var(--color-app-950) 0%, transparent 34%);
		pointer-events: none;
	}
	/* Portrait: the copy stacks over the part, so the whole scene sits back. */
	@media (max-width: 720px) {
		.hero__shade {
			background: linear-gradient(0deg, var(--color-app-950) 8%, rgba(12, 13, 11, 0.62) 100%);
		}
	}
	.hero__inner,
	.hero__figures {
		position: relative;
		z-index: 1;
		width: 100%;
		max-width: var(--container-page);
		margin-inline: auto;
		padding-inline: var(--spacing-gutter);
	}
	.hero__inner {
		padding-top: clamp(7rem, 16vh, 11rem);
	}
	.hero__figures {
		padding-bottom: clamp(2rem, 5vh, 3.5rem);
	}
	.lockup {
		display: flex;
		align-items: center;
		gap: 1rem;
		margin-bottom: clamp(2rem, 5vh, 3.5rem);
	}
	.lockup__logo {
		height: 2.75rem;
		width: auto;
	}
	.lockup__x {
		font-family: var(--font-mono);
		color: var(--color-bone-500);
	}
	.lockup__us {
		font-size: 1.125rem;
		color: var(--color-bone-100);
		--logo-accent: var(--color-cut-400);
	}
	/* Two logos side by side: on a phone both step down so the pair stays on one line. */
	@media (max-width: 520px) {
		.lockup {
			gap: 0.7rem;
		}
		.lockup__logo {
			height: 2rem;
		}
		.lockup__us {
			font-size: 0.8125rem;
		}
	}
	.kicker {
		display: inline-flex;
		align-items: center;
		gap: 0.6rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.kicker__tick {
		flex: none;
		width: 0.5rem;
		height: 0.5rem;
		border-radius: 999px;
		background: var(--color-cut-400);
		box-shadow: 0 0 0 4px color-mix(in srgb, var(--color-cut-400) 22%, transparent);
	}
	h1 {
		margin-top: 1.25rem;
		max-width: 15ch;
		font-size: clamp(2.5rem, 5.4vw, 4.75rem);
		line-height: var(--text-display--line-height);
		letter-spacing: var(--text-display--letter-spacing);
		font-weight: var(--text-display--font-weight);
		color: var(--color-bone-100);
		text-wrap: balance;
	}
	.lede {
		margin-top: 1.75rem;
		max-width: 44ch;
		font-size: var(--text-lead);
		line-height: var(--text-lead--line-height);
		letter-spacing: var(--text-lead--letter-spacing);
		color: var(--color-bone-500);
		text-wrap: pretty;
	}
	.actions {
		display: flex;
		flex-wrap: wrap;
		gap: 0.75rem;
		margin-top: 2rem;
	}
	:global(.cs-figures) {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 1.5rem 2rem;
		margin-top: clamp(2.5rem, 6vh, 4.5rem);
		padding-top: 1.5rem;
		border-top: 1px solid var(--color-app-700);
	}
	@media (min-width: 800px) {
		:global(.cs-figures) {
			grid-template-columns: 1.4fr 1fr 1fr 1fr;
		}
	}

	/* ── The job ──────────────────────────────────────────────────────── */
	.job {
		display: grid;
		gap: clamp(2.5rem, 6vw, 5rem);
		align-items: start;
	}
	@media (min-width: 900px) {
		.job {
			grid-template-columns: minmax(0, 1.25fr) minmax(0, 0.75fr);
		}
	}
	.argument {
		display: flex;
		flex-direction: column;
		gap: 1.5em;
		margin-top: clamp(2.5rem, 4vw, 3.5rem);
		max-width: 30ch;
		font-size: clamp(1.375rem, 2.6vw, 2rem);
		font-weight: 500;
		line-height: 1.35;
		letter-spacing: -0.02em;
	}

	:global(.cs-photo) {
		position: relative;
	}
	:global(.cs-photo) img {
		display: block;
		width: 100%;
		height: auto;
		object-fit: cover;
		border-radius: var(--radius-win);
		background: var(--color-app-900);
	}
	:global(.cs-photo--truck) img {
		aspect-ratio: 4 / 5;
		object-position: 50% 58%;
		box-shadow: var(--shadow-lift);
	}
	:global(.cs-photo--film) img {
		aspect-ratio: 4 / 5;
		object-position: 50% 45%;
		box-shadow: var(--shadow-lift);
	}
	:global(.cs-photo--recess) img {
		aspect-ratio: 5 / 4;
		object-position: 50% 42%;
		border: 1px solid var(--color-app-700);
	}
	.photo__cap {
		margin-top: 0.75rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		color: var(--color-ink-400);
		text-wrap: pretty;
	}
	.photo__cap--dark {
		color: var(--color-bone-500);
	}

	/* The ledger: a rule, an index, a label, a claim. Same as the landing page. */
	.ledger {
		display: grid;
		gap: 2.5rem;
		margin-top: clamp(3.5rem, 7vw, 6rem);
	}
	@media (min-width: 800px) {
		.ledger {
			grid-template-columns: repeat(3, minmax(0, 1fr));
			gap: 3rem;
		}
		.ledger--files {
			grid-template-columns: repeat(4, minmax(0, 1fr));
			gap: 2.25rem;
		}
	}
	.ledger :global(li) {
		padding-top: 1.25rem;
		border-top: 1px solid var(--color-line-strong);
	}
	.ledger__index {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		color: var(--color-ink-400);
	}
	.ledger__index--ext {
		font-size: 1.5rem;
		letter-spacing: -0.01em;
		color: var(--color-cut-700);
	}
	.ledger__label {
		margin-top: 0.35rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-cut-700);
	}
	.ledger__title {
		margin-top: 1rem;
		font-size: var(--text-h3);
		line-height: var(--text-h3--line-height);
		letter-spacing: var(--text-h3--letter-spacing);
		font-weight: 600;
		color: var(--color-ink-900);
		text-wrap: balance;
	}
	.ledger__body {
		margin-top: 0.75rem;
		color: var(--color-ink-500);
		text-wrap: pretty;
	}

	/* ── The file ─────────────────────────────────────────────────────── */
	.file-head {
		display: grid;
		gap: 2.5rem;
		align-items: end;
	}
	@media (min-width: 900px) {
		.file-head {
			grid-template-columns: minmax(0, 1.4fr) minmax(0, 0.6fr);
			gap: clamp(3rem, 8vw, 7rem);
		}
	}
	.dark-h2 {
		margin-top: 1.25rem;
		max-width: 16ch;
		font-size: var(--text-h2);
		line-height: var(--text-h2--line-height);
		letter-spacing: var(--text-h2--letter-spacing);
		font-weight: var(--text-h2--font-weight);
		color: var(--color-bone-100);
		text-wrap: balance;
	}
	.dark-h2--xl {
		font-size: clamp(2.5rem, 5.4vw, 4.75rem);
		line-height: var(--text-display--line-height);
		letter-spacing: var(--text-display--letter-spacing);
	}
	:global(.cs-asis) {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 1.5rem 2rem;
		margin-top: clamp(2rem, 4vw, 3rem);
		padding-top: 1.5rem;
		border-top: 1px solid var(--color-app-700);
	}
	@media (min-width: 800px) {
		:global(.cs-asis) {
			grid-template-columns: repeat(4, minmax(0, 1fr));
		}
	}
	.steps {
		display: grid;
		gap: 2.5rem;
		margin-top: clamp(3.5rem, 7vw, 5.5rem);
		counter-reset: none;
	}
	@media (min-width: 700px) {
		.steps {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
	@media (min-width: 1100px) {
		.steps {
			grid-template-columns: repeat(4, minmax(0, 1fr));
			gap: 2.25rem;
		}
	}
	.steps :global(li) {
		padding-top: 1.25rem;
		border-top: 1px solid var(--color-app-700);
	}
	.steps__index {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		color: var(--color-cut-400);
	}
	.steps__title {
		margin-top: 0.85rem;
		font-size: var(--text-h3);
		line-height: var(--text-h3--line-height);
		letter-spacing: var(--text-h3--letter-spacing);
		font-weight: 600;
		color: var(--color-bone-100);
		text-wrap: balance;
	}
	.steps__body {
		margin-top: 0.75rem;
		color: var(--color-bone-500);
		text-wrap: pretty;
	}

	/* ── The result ───────────────────────────────────────────────────── */
	.result {
		display: grid;
		gap: clamp(2.5rem, 6vw, 5rem);
		align-items: center;
	}
	@media (min-width: 900px) {
		.result {
			grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
		}
	}
	.result__lead {
		margin-top: 1.75rem;
		max-width: 46ch;
		font-size: var(--text-lead);
		line-height: var(--text-lead--line-height);
		letter-spacing: var(--text-lead--letter-spacing);
		color: var(--color-ink-700);
		text-wrap: pretty;
	}
	.result__rows {
		margin-block: 2.25rem 2.5rem;
		border-top: 1px solid var(--color-line-strong);
	}
	.result__rows div {
		display: grid;
		grid-template-columns: 7rem 1fr;
		gap: 1rem;
		padding-block: 1rem;
		border-bottom: 1px solid var(--color-line);
	}
	.result__rows dt {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-ink-400);
		padding-top: 0.2em;
	}
	.result__rows dd {
		color: var(--color-ink-700);
		text-wrap: pretty;
	}
	.customer {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 0.75rem 1.5rem;
		margin-top: 1rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		color: var(--color-ink-500);
	}
	.customer img {
		height: 3rem;
		width: auto;
	}
	.customer:hover span {
		color: var(--color-ink-900);
	}

	/* ── The close ────────────────────────────────────────────────────── */
	.close {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
	}
</style>
